import React from 'react';
import { safeFormatDate } from '../../../shared';
import { Box, Typography, Paper } from '@mui/material';
import CopyIconButton from '../../../shared/components/CopyIconButton';
import { RoleValidationResult } from '../../../types/backend';

interface RoleTestResultPanelProps {
  testResult: RoleValidationResult;
}

const RoleTestResultPanel: React.FC<RoleTestResultPanelProps> = ({ testResult }) => {
  const roleLabel =
    testResult.role_type === 'readonly' ? 'Read-Only' :
    testResult.role_type === 'automation' ? 'Automation' : 'Write';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: testResult.success ? 'success.lighter' : 'error.lighter',
        borderColor: testResult.success ? 'success.main' : 'error.main'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {testResult.success
            ? `${roleLabel} Role Test Successful`
            : `${roleLabel} Role Test Failed`}
        </Typography>
        <CopyIconButton
          text={JSON.stringify(testResult, null, 2)}
          tooltip="Copy to clipboard"
        />
      </Box>

      {!testResult.success ? (
        <>
          <Typography variant="body2" sx={{ mb: 1, color: 'error.dark' }}>
            <strong>Error:</strong> {testResult.error_message}
          </Typography>
          {testResult.failed_at_hop !== null && testResult.failed_at_hop !== undefined && (
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Failed at: {
                testResult.failed_at_hop === 0 ? 'Pre-validation (configuration error)' :
                testResult.failed_at_hop === 1 ? 'Hop 1 (Lambda to Operations Account)' :
                testResult.failed_at_hop === 2 ? 'Hop 2 (Operations Account to Customer Account)' :
                testResult.failed_at_hop === 3 ? 'Test operation execution' :
                'Unknown'
              }
            </Typography>
          )}

          {testResult.hop1_operations_account && (
            <HopResultBox
              label="Hop 1: Lambda to Operations Account"
              hop={testResult.hop1_operations_account}
            />
          )}

          {testResult.hop2_customer_account && (
            <HopResultBox
              label="Hop 2: Operations Account to Customer Account"
              hop={testResult.hop2_customer_account}
              sx={{ mt: 1 }}
            />
          )}
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Successfully assumed {roleLabel.toLowerCase()} role in customer account
          </Typography>

          {testResult.hop1_operations_account && (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                Hop 1: Lambda to Operations Account
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', display: 'block', color: 'text.secondary' }}>
                {testResult.hop1_operations_account.role_arn}
              </Typography>
              {testResult.hop1_operations_account.expiration && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', color: 'text.secondary', mt: 0.5 }}>
                  Expires: {safeFormatDate(testResult.hop1_operations_account.expiration)}
                </Typography>
              )}
            </Box>
          )}

          {testResult.hop2_customer_account && (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                Hop 2: Operations Account to Customer Account
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', display: 'block', color: 'text.secondary' }}>
                {testResult.hop2_customer_account.role_arn}
              </Typography>
              {testResult.hop2_customer_account.expiration && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', color: 'text.secondary', mt: 0.5 }}>
                  Expires: {safeFormatDate(testResult.hop2_customer_account.expiration)}
                </Typography>
              )}
            </Box>
          )}

          {testResult.operation_result && (
            <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                Test Operation Result ({testResult.aws_operation.service_name}.{testResult.aws_operation.operation_name}):
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(testResult.operation_result, null, 2)}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

interface HopResultBoxProps {
  label: string;
  hop: { success: boolean; role_arn?: string; error_message?: string };
  sx?: object;
}

const HopResultBox: React.FC<HopResultBoxProps> = ({ label, hop, sx }) => (
  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, ...sx }}>
    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
      {label}
    </Typography>
    {hop.success ? (
      <Typography variant="caption" sx={{ color: 'success.main' }}>
        Success - Role: {hop.role_arn}
      </Typography>
    ) : (
      <Typography variant="caption" sx={{ color: 'error.dark', display: 'block' }}>
        Failed: {hop.error_message}
      </Typography>
    )}
  </Box>
);

export default RoleTestResultPanel;
